import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Button, Caption1, Label, InfoSmallIcon, Tooltip } from "@activecollab/components";
import { Formik, Form, Field } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import i18n from "~/i18n";
import { trimFormValues } from "~/utils/utils.jsx";
import { useFocusInvalidField } from "../Hooks/useFocusInvalidField.js";
import { InputWithSelectedText } from "../Inputs/InputWithSelectedText.jsx";

export const ActionObjectDialog = ({
  name,
  open,
  title,
  labelText,
  explainerText,
  placeHolderText,
  submitButtonText,
  onSubmit,
  onCancel,
  limit,
}) => {
  const { t } = useTranslation();
  const formikRef = useRef();

  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required("Name is required"),
  });

  const handleDialogSubmit = useCallback(
    (values) => {
      if (typeof onSubmit === "function") {
        const data = trimFormValues(values);
        onSubmit(data, formikRef);
      }
    },
    [onSubmit]
  );

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <Formik
        initialValues={{ name }}
        innerRef={formikRef}
        validationSchema={validationSchema}
        onSubmit={handleDialogSubmit}
        validateOnMount
      >
        {({ isSubmitting, errors, touched, handleSubmit }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();
            handleSubmit();
            if (!Object.keys(errors)?.length) return;
            focusFirstInvalidField(errors, false);
          };
          return (
            <Form onSubmit={handleFormSubmit}>
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content>
                <Field name="name">
                  {({ field, form }) => (
                    <>
                      <Label invalid={errors.name && touched.name}>{labelText}</Label>
                      <InputWithSelectedText
                        name={field.name}
                        className="tw-w-full tw-mt-1"
                        placeholder={placeHolderText}
                        onChange={(e) => {
                          form.setFieldTouched(field.name, false);
                          form.setFieldValue(field.name, e.target.value);
                        }}
                        value={field.value}
                        invalid={errors.name && touched.name}
                        ref={fieldRef}
                        {...(limit && { maxLength: limit })}
                        endAdornment={
                          touched.name && errors.name ? (
                            <Tooltip title={errors.name}>
                              <InfoSmallIcon
                                fill="var(--red-alert)"
                                className="tw-cursor-help tw-mr-1"
                              />
                            </Tooltip>
                          ) : null
                        }
                        autoFocus
                      />
                    </>
                  )}
                </Field>
                {explainerText.length ? (
                  <Caption1
                    color="tertiary"
                    className="tw-mt-2"
                  >
                    {explainerText}
                  </Caption1>
                ) : null}
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  className="tw-mr-3"
                >
                  {submitButtonText}
                </Button>
                <Button
                  type="button"
                  onClick={onCancel}
                  variant="secondary"
                  disabled={isSubmitting}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

ActionObjectDialog.propTypes = {
  name: PropTypes.string,
  open: PropTypes.bool,
  title: PropTypes.string,
  labelText: PropTypes.string,
  explainerText: PropTypes.string,
  submitButtonText: PropTypes.string,
  placeHolderText: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  limit: PropTypes.number,
};

ActionObjectDialog.defaultProps = {
  name: "",
  open: false,
  title: i18n.t("Title"),
  labelText: i18n.t("New object name"),
  explainerText: "",
  submitButtonText: "Submit",
  placeHolderText: "",
};
